import type { QuestionStep, SurveyLinkMode, SurveyStep } from '../types/survey.js';

// Phase 6B (NEW-2) — the auto-inserted "name" step normalizer. PURE, shared by
// client + server. The client applies it the instant the AM toggles link mode
// (instant UI preview + correct live validateSurveyFlow); the server re-applies
// it idempotently on every PATCH as the authority. Because it's idempotent,
// running it on both sides can never drift.
//
// Rule (§3.5): per_location surveys carry exactly one system-managed name step
// (`type: 'name'`, `isAutoInserted: true`, required) at index 0 — it captures
// who responded when the link is shared at the location level. per_recipient
// surveys carry none (the recipient is already known from the token).

const AUTO_NAME_PROMPT = 'Your name';

// Generate a fresh auto-name step. The client owns step-id generation (it
// inserts the step on link-mode change for preview); the server only mints one
// here when a per_location draft arrives without it.
export function makeAutoNameStep(): QuestionStep {
  return {
    kind: 'question',
    id: crypto.randomUUID(),
    type: 'name',
    required: true,
    prompt: AUTO_NAME_PROMPT,
    description: null,
    config: { type: 'name' },
    branching: [],
    isAutoInserted: true,
  };
}

function isAutoName(step: SurveyStep): boolean {
  return step.kind === 'question' && step.isAutoInserted;
}

// Return `steps` normalized for `linkMode`. Idempotent:
//   per_location  → exactly one auto-name step at index 0 (existing one is
//                   preserved + moved to front; otherwise a fresh one is minted).
//   per_recipient → all auto-inserted steps stripped.
// The non-auto steps keep their relative order; branching references (by stepId)
// are unaffected since the auto-name step is never a branch target (it's first,
// and references are forward-only).
export function normalizeSurveySteps(
  steps: SurveyStep[],
  linkMode: SurveyLinkMode,
): SurveyStep[] {
  const rest = steps.filter((s) => !isAutoName(s));
  if (linkMode === 'per_recipient') return rest;
  const existing = steps.find((s): s is QuestionStep => isAutoName(s));
  return [existing ?? makeAutoNameStep(), ...rest];
}
